import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import ProjetosPropostasComponentesDom, { ComponentesFinameDom, ProjetosPropostasCupomDom, RenderPropsSimplificada } from './types';
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, useTheme } from '@mui/material';
import { cnpjCpf } from 'src/framework/commons/masks';
import { useHistory, useParams } from 'react-router-dom';
import { getPropostaSimplificada } from 'src/store/global.store';
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import './styles.css';
import PaginaLogoIntelbrasPropostaSimplificada from './PaginaLogoIntelbrasPropostaSimplificada';
import CondicoesPagamento from './CondicoesPagamento';
import Garantias from './Garantias';
import { DecimalUtil } from 'src/framework/commons/DecimalUtils';
import { DateUtil } from 'src/framework/commons/DateUtils';
import { propostasLoading$ } from 'src/framework/store/ApiEvents';
import { isFirefox, isMobile } from 'src/components/projetos/utils/deviceUtils';
import { TCategoriasComponentes } from 'src/components/projetos/types';
import { ORDEM_GRUPO_CATEGORIAS } from 'src/components/projetos/constants';
import { TGrupoCategoria } from 'src/components/componentes/types';

type RouteParams = {
  hash: string;
};

const PropostaSimplificada: React.FC = () => {
    const [data, setData] = useState<RenderPropsSimplificada>();
    const { hash } = useParams<RouteParams>();

    let contadorPagina = 1;

    const componentRef = useRef<HTMLDivElement | null>(null);
    const history = useHistory<{ sValores?: boolean; }>();

    const theme = useTheme();
    const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

    const avulso = useMemo(()=>{
        return data?.projeto?.tipo === "avulso";
    }, [data?.projeto?.tipo])

    const diasExpiracao = useMemo(()=>{
        const parametros = data?.parametros || [];
        if (parametros.length === 0) return null;
        const paramExpiracao = parametros.filter((obj: any) => obj.id === "PROJETOS_DIAS_EXPIRACAO")
        if (!paramExpiracao) return null;
        const dias = paramExpiracao[0].valor ? paramExpiracao[0].valor === "0" ? 1 : paramExpiracao[0].valor : 1;
        return Number(dias) === 1 ? `${dias} dia` : `${dias} dias`;
    },[data?.parametros])

    const diasCancelamentoAvulso = useMemo(()=>{
        const parametros = data?.parametros || [];
        if (parametros.length === 0) return null;
        const paramCancelamento = parametros.find((obj: any) => obj.id === "PROJETOS_AVULSO_DIAS_CANCELAMENTO")
        if (!paramCancelamento) return null;
        const dias = paramCancelamento.valor ? paramCancelamento.valor === "0" ? 1 : paramCancelamento.valor : 1;
        return Number(dias) === 1 ? `${dias} dia` : `${dias} dias`;
    }, [data?.parametros])

    useEffect(()=>{
        propostasLoading$.next(true);
    },[])

    useEffect(() => {
        document.body.classList.add('proposta-web');
    }, []);

    const addClassPage = useCallback(() => {
        const pages = document.querySelectorAll('.page');
        pages.forEach((el, idx) => {
            el.className = el?.className?.split(' ')?.filter(c => !c.startsWith('pagina-'))?.join(' ');
            el.classList.add(`pagina-${idx + 1}`);

            const span = el.querySelector(".page-number");
            if (span) {
                span.textContent = String(idx + 1);
            }
        });
    }, []);

    useEffect(() => {
        const loadProposta = async () => {
            const propostaSimplificada = await getPropostaSimplificada(hash);
            if (propostaSimplificada?.projeto) {
                setData(propostaSimplificada);
                addClassPage();
                propostasLoading$.next(false)
            } else {
                propostasLoading$.next(false)
            }

            return propostaSimplificada;
        }

        if (hash) {
            loadProposta();
        }
    }, [hash, addClassPage, history.location]);

    const textEndereco: string = useMemo(() => {
        let text = "";
        if (data?.cliente) {
            const dados = data?.cliente[0];
            if (!dados?.logradouro || !dados?.bairro || !dados?.cidade || !dados?.estado) {
                text = "-";
            } else {
                text += dados?.logradouro;
                text += dados?.numero ? `, ${dados?.numero}` : ", SN";
                if (dados?.bairro) text += ` - Bairro: ${dados?.bairro}`;
                if (dados?.cidade && dados?.estado) text += ` - ${dados?.cidade}/${dados?.estado}`;
                if (dados?.cep) text += ` - CEP: ${dados?.cep.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1')}`;
            }
        }
        return text;
    }, [data?.cliente]);

    const textCupom: string = useMemo(() => {
        let text = "";
            if (data?.cupom && !!data?.cupom?.length) {
                data?.cupom?.forEach((cupom: ProjetosPropostasCupomDom) => {
                    if (cupom.cupom_desconto && cupom.data_expiracao_cupom_desconto) {
                        text += `${cupom.cupom_desconto}-${DateUtil.date(cupom.data_expiracao_cupom_desconto.toString())}`;
                    }
                });
            }
        return text;
    }, [data?.cupom]);

    const totalDiscount: number = useMemo(() => {
        let desconto = 0;
        if (data?.cupom && !!data?.cupom?.length) {
            data?.cupom?.forEach((cupom: ProjetosPropostasCupomDom) => {
                if (cupom.valor_desconto && typeof cupom.valor_desconto === "number") {
                    desconto += cupom.valor_desconto;
                }
            });
        }
        return desconto;
    }, [data?.cupom]);

    const categoriasComponentes: TCategoriasComponentes[] = useMemo(()=>{
        if(data?.componentesCategorias && !!data?.componentesCategorias?.length){
            return data?.componentesCategorias || [];
        }
        return [];
    }, [data?.componentesCategorias])

    const ordenarComponentesPorCategoria = (
        items: ProjetosPropostasComponentesDom[],
    ) => {
        return items?.map(item => {
            const categoria = categoriasComponentes?.find((i: TCategoriasComponentes) => i.id === item?.categoria_componentes_id);
            return {
                ...item,
                nome_categoria: categoria?.nome || "",
                grupo_categoria: categoria?.grupo || "",
                ordem_listagem: categoria?.ordem_listagem || item?.categoria_componentes_id,
            };
        })?.sort((a, b) => {
                const pesoGrupoA = ORDEM_GRUPO_CATEGORIAS[a.grupo_categoria as TGrupoCategoria] || a?.categoria_componentes_id;
                const pesoGrupoB = ORDEM_GRUPO_CATEGORIAS[b.grupo_categoria as TGrupoCategoria] || b?.categoria_componentes_id;
                if (pesoGrupoA !== pesoGrupoB) {
                    return pesoGrupoA - pesoGrupoB;
                }
                return a.ordem_listagem - b.ordem_listagem;
            });
    };

    const calculaRateio  = useMemo(() => {
        if (data?.projeto && data?.componentes && (totalDiscount > 0 || Number(data?.projeto?.valor_desconto) > 0)) {
            const componentes = [...data?.componentes];
            const valorTotalRateio = data?.projeto?.valor;
            const valorDesconto = Number(data?.projeto?.valor_desconto) + Number(totalDiscount);
            const percentualDesconto =  DecimalUtil.toFixed(valorDesconto / valorTotalRateio, 2);

            const componentesResult = [];

            for (const idx in componentes) {
                const valorItem = componentes[idx].valor_unitario;
                const descontoItem =  DecimalUtil.toFixed(valorItem * percentualDesconto, 2);
                let novoValor = valorItem - descontoItem;

                // Garantir que o valor não fique zerado ou negativo
                if (novoValor <= 0) novoValor = 0.01;

                const componente = {...componentes[idx]};
                componente.valor_unitario = DecimalUtil.toFixed(novoValor, 2);
                componentesResult.push({...componente});
            };

            const valorTotalEsperado = (data?.projeto?.valor - data?.projeto?.valor_desconto - totalDiscount);
            const somaAtual = Number(componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0).toFixed(2));
            let diferenca = DecimalUtil.toFixed(valorTotalEsperado - somaAtual, 2);

            if (diferenca !== 0) {
                const itemMaiorValorUnico = componentesResult
                ?.sort((a, b) => {
                    if (b.valor_unitario !== a.valor_unitario) return b.valor_unitario - a.valor_unitario;
                    return a.quantidade - b.quantidade;
                })[0];

                if (itemMaiorValorUnico) {
                    const index = componentesResult.findIndex(i => i.id === itemMaiorValorUnico.id);
                    if (index > -1) {
                        const novoValorCorrigido = Number(itemMaiorValorUnico.valor_unitario) + Number(diferenca / itemMaiorValorUnico.quantidade);
                        componentesResult[index].valor_unitario = Number(novoValorCorrigido.toFixed(2));
                    }
                }
            }

            // Normalização para eliminar diferenças por arredondamento
            let totalFinal = Number(componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0).toFixed(2));
            totalFinal = Math.round(totalFinal * 100) / 100;
            const valorEsperadoNormalizado = Math.round(valorTotalEsperado * 100) / 100;
            let diferencaFinal = DecimalUtil.toFixed(valorEsperadoNormalizado - totalFinal, 2);

            if (Math.abs(diferencaFinal) >= 0.01) {
                const itemAjusteFinal = componentesResult
                ?.sort((a, b) => {
                    if (b.valor_unitario !== a.valor_unitario) return b.valor_unitario - a.valor_unitario;
                    return a.quantidade - b.quantidade;
                })[0];

                if (itemAjusteFinal) {
                    const index = componentesResult.findIndex(i => i.id === itemAjusteFinal.id);
                    if (index > -1) {
                        const novoValorFinal = componentesResult[index].valor_unitario + (diferencaFinal / componentesResult[index].quantidade);
                        componentesResult[index].valor_unitario = Number(novoValorFinal.toFixed(2));
                    }
                }

                totalFinal = Math.round(
                    componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0) * 100
                ) / 100;
            }
            return ordenarComponentesPorCategoria(componentesResult);
        }
        return ordenarComponentesPorCategoria(data?.componentes || []);
    }, [data?.projeto, data?.componentes, totalDiscount, categoriasComponentes]);
    
    const componenteFiname: string = useMemo(() => {
        return data?.projeto?.componentes_finame?.find((cf: ComponentesFinameDom) => cf)?.codigo_finame || '';
    }, [data?.projeto?.componentes_finame]);

    const valorProjeto: number = useMemo(() => {
        return (data?.projeto?.valor_projeto || 0) - totalDiscount || 0;
    }, [data?.projeto?.valor_projeto, totalDiscount]);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        preserveAfterPrint: true,
        documentTitle: `Proposta_${data?.projeto?.codigo_venda}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 12mm 0;
            }
            body {
                margin: 0;
                padding: 0;  
                background: white;
                -webkit-print-color-adjust: exact;
                font-family: sans-serif;
            }
            *, html, body {
                font-size: 12pt !important;
                font-family: var(--intelbras-font) !important;
            }
            .page td {
                font-family: var(--intelbras-font) !important
            }
        `, 
    });


    const handlePaginacao = () => {
        const pagina = contadorPagina;
        contadorPagina++;

        return pagina;
    }

    if (!data)  {
        return <></>;
    };

    return (
        <div className='root-simplificada'>
            <div className={`page-container ${(isFirefox() || isMobile()) ? "page-firefox" : ""}`} ref={componentRef} >
                {/* <div className="page"> */}
                <PaginaLogoIntelbrasPropostaSimplificada first logo={data.imagens.logo} pagina={handlePaginacao()}>
                    <Box display={"flex"} flexDirection="row" width={"100%"} border="1px solid #000" className="box-simplificada">
                        <Box width={isTabletOrSmaller ? "100%" : "65%"} borderRight={isTabletOrSmaller ? undefined : "1px solid #000"} >
                            <Box display="flex" flexDirection={"column"} textAlign={"left"} padding={"4px"} alignItems={"start"} >
                                {/* Dados */}
                                <div style={{ marginBottom: "24px" }} className="box-simplificada-titulo">
                                    <h2 style={{ marginBottom: "4px", marginTop: 2 }} ><strong>PROPOSTA SIMPLIFICADA</strong></h2>
                                    <span>Proposta comercial nº <strong>{data?.projeto?.codigo_venda}</strong></span>
                                </div>

                                <span style={{ marginBottom: "12px" }} className="box-simplificada-texto"><strong>Data criação:</strong> {DateUtil.date(data?.projeto?.created_at || "")}</span>
                                <span style={{ marginBottom: "12px" }} className="box-simplificada-texto"><strong>Razão Social:</strong> INTELBRAS S.A IND DE TELECOMUNICACAO ELETRONICA BRASILEIRA</span>
                                <span style={{ marginBottom: "12px" }} className="box-simplificada-texto"><strong>CNPJ:</strong> 82.901.000/0001-27</span>
                                <span style={{ marginBottom: "12px" }} className="box-simplificada-texto"><strong>Endereço:</strong> RODOVIA BR 101 SN - Bairro: AREA INDUSTRIAL - São José/SC</span>
                            </Box>
                        </Box>

                        {!isTabletOrSmaller && <Box display={"flex"} width={"35%"} justifyContent="center" padding={"4px"} className="print-image-wrapper">
                            {/* Image */}
                            <img style={{ objectFit: "cover", width: "100%" }}  src={data.imagens.capa} alt="Logo Intelbras Capa"></img>
                        </Box>}
                    </Box>
            
                    {!!data?.cliente?.length && (
                        <div>
                            <p className="subTitle bg-verde-intelbras text-white">Dados do cliente</p>

                            <p className="labelText">Nome/Razão Social:</p>                        
                            <p className="defaultText">{data?.cliente[0]?.nome_cliente || "-"}</p>

                            {!avulso && (<>
                                <p className="labelText">CPF/CNPJ:{" "}</p>
                                <p className="defaultText">
                                    {data?.cliente[0]?.documento_cliente
                                        ? cnpjCpf(data?.cliente[0].documento_cliente)
                                        : "-"}
                                </p>
                            </>)}

                            {avulso ? <p className="labelText">Estado (UF):</p> : <p className="labelText">Endereço:</p>}                    
                            <p className="defaultText">{textEndereco}</p>
                        </div>
                    )}

                    <br />
                    <div className="default-text-list"><strong>Compreende no orçamento do projeto{componenteFiname ? ` - ${componenteFiname}:` : ":"}</strong></div>

                <div>
                    <Table component={Paper} size="small" className="table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ textAlign: "left" }} className="tableColEquipamentoHead bg-verde-intelbras text-white font-intelbras-bold">Equipamento</TableCell>
                                <TableCell className="tableColMarcaHead bg-verde-intelbras text-white font-intelbras-bold">Marca</TableCell>
                                <TableCell className="tableColQuantidadesHead bg-verde-intelbras text-white font-intelbras-bold">Quant.</TableCell>
                                {data?.valoresUnitarios && (
                                    <>
                                        <TableCell className="tableColValoresUnitariosHead bg-verde-intelbras text-white font-intelbras-bold">Valor Final</TableCell>
                                        <TableCell className="tableColServicoInstalacaoHead bg-verde-intelbras text-white font-intelbras-bold ">Total Final</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {calculaRateio?.map((comp, index) => (
                                <TableRow key={index}>
                                    <TableCell className={'tableColEquipamento'}>
                                        <span className="tableCellEquipamentos">{comp.nome}</span>
                                        <br />
                                        {comp.descricao && <small>{comp.descricao}</small>}
                                    </TableCell>
                                    <TableCell className={'tableColMarca'}>
                                        <span className="tableCellMarca">{comp.parceiro || ""}</span>
                                    </TableCell>
                                    <TableCell className={'tableColQuantidades'}>
                                        <span className="tableCellQuantidades">{comp.quantidade}</span>
                                    </TableCell>
                                    {data?.valoresUnitarios && (
                                        <>
                                            <TableCell className={`tableColValUnitarios`}>
                                                <span className="tableCellValor">R$ {DecimalUtil.decimal(comp.valor_unitario)}</span>
                                            </TableCell>
                                            <TableCell className={'tableColValUnitarios'}>
                                                <span className="tableCellValor">R$ {DecimalUtil.decimal(comp.valor_unitario * comp.quantidade)}</span>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                <br/>
                <br/>

                <Table component={Paper} size="small" className="table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ textAlign: "left" }} className="tableColEquipamentoHead bg-verde-intelbras text-white font-intelbras-bold" colSpan={2} align="left" >Valor do investimento</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>

                        {textCupom && totalDiscount > 0 && (
                                <>
                                <TableRow>
                                    <TableCell className={'tableColCustoTotal'}>
                                        <span className="custoTotalTexto">Valor</span>
                                    </TableCell>
                                    <TableCell className={`tableColCustoTotalValor`}>
                                            R$ {DecimalUtil.decimal(valorProjeto + totalDiscount)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={'tableColCustoTotal'}>
                                        <span className="custoTotalTexto">Cupom de Desconto</span>
                                    </TableCell>
                                    <TableCell className={`tableColCustoTotalValor`}>
                                        - R$ {DecimalUtil.decimal(totalDiscount)}
                                    </TableCell>
                                </TableRow>
                                </>
                            )}

                            <TableRow>
                                <TableCell className={'tableColCustoTotal'}>
                                    <span className="custoTotalTexto font-intelbras-bold">TOTAL</span>
                                </TableCell>
                                <TableCell className={`tableColCustoTotalValor font-intelbras-bold`}>
                                    R$ {DecimalUtil.decimal(valorProjeto)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <br/>
                    <br/>
                    <div className='default-text'  style={{ textAlign: "left", display: "block" }}>
                        {avulso ? <small>* Este é um orçamento que tem validade de {diasCancelamentoAvulso ? diasCancelamentoAvulso : ""}, ou enquanto durarem os estoques. A Intelbras pode, constatada a compra e a seu exclusivo critério, encaminhar algum produto igual ou superior ao especificado acima, caso haja disponibilidade de estoque dos produtos, sem ônus adicional, estando o cliente ciente e de acordo com essas condições.</small> : <small>* Este é um pré-orçamento e tem validade de {diasExpiracao ? diasExpiracao : "15 (quinze) dias"}, ou enquanto durarem os estoques. A Intelbras pode, constatada a compra e a seu exclusivo critério, encaminhar um sistema com potencial de geração igual ou superior ao especificado acima, caso haja disponibilidade de estoque dos produtos, sem ônus adicional, estando o cliente ciente e de acordo com essas condições.</small>}
                    </div>
                {/* </div> */}
                </PaginaLogoIntelbrasPropostaSimplificada>

                <PaginaLogoIntelbrasPropostaSimplificada logo={data.imagens.logo} pagina={handlePaginacao()}>
                    <CondicoesPagamento dadosParcela={data.valorParcelasCartao} dados={data.projeto} />
                    <Garantias informacoesGarantia={data?.informacoesGarantia || []} temServico={(data?.projeto?.valor_servico || 0) > 0} componentes={data.componentes} segmento_projeto={data?.projeto?.segmento?.includes("on") ? "on" : "off"} avulso={data?.projeto?.tipo === "avulso"} />
                </PaginaLogoIntelbrasPropostaSimplificada>
            </div>

            <button className="print-button" onClick={handlePrint}>
                { !isTabletOrSmaller 
                    ? (<>IMPRIMIR <br /> PROPOSTA</>)
                    : (<PrintIcon style={{ fontSize: 33 }}  />) 
                }
            </button>
        </div>
    )
};

export default PropostaSimplificada;
